import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para la configuración de la API key de Anthropic Claude
 * por profile (M1.10). Mismo patrón que `strava.ts`: usamos `fetch` directo
 * (con `credentials: 'include'`) en lugar del helper `request` porque los
 * endpoints tienen un mapeo de errores muy específico (503 con varios
 * sub-códigos, 400 con `issues.fieldErrors`).
 *
 * NOTA: la apiKey NUNCA viaja en respuestas GET; sólo se envía en el PUT.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Modelos soportados
// ---------------------------------------------------------------------------

export const ANTHROPIC_MODELS = [
  'claude-sonnet-4-6',
  'claude-haiku-4-5',
  'claude-opus-4-6',
] as const;
export type AnthropicModel = (typeof ANTHROPIC_MODELS)[number];

/**
 * Estimación conservadora del coste tokens/EUR usada también por el backend
 * (~200k tokens combinados por EUR). Exportada para que la UI pueda calcular
 * el porcentaje de presupuesto consumido y la conversión inversa
 * (tokens → EUR aproximado).
 */
export const TOKENS_PER_EUR = 200_000;

// ---------------------------------------------------------------------------
// Errores específicos
// ---------------------------------------------------------------------------

/** Backend no tiene API key de Anthropic configurada para este profile. */
export class AnthropicNotConfiguredError extends ApiError {
  public readonly hint: string;
  constructor(hint = '') {
    super('Anthropic no configurado en el backend', 503);
    this.name = 'AnthropicNotConfiguredError';
    this.hint = hint;
  }
}

/** El profile alcanzó su presupuesto mensual de tokens Anthropic. */
export class AnthropicBudgetExceededError extends ApiError {
  public readonly hint: string;
  constructor(hint = '') {
    super('Presupuesto mensual de Anthropic agotado', 503);
    this.name = 'AnthropicBudgetExceededError';
    this.hint = hint;
  }
}

/**
 * Error de validación 400 del backend: `{ error, issues: { fieldErrors, formErrors } }`
 * tal y como devuelve `z.flatten()`. Replica de `StravaAppConfigValidationError`.
 */
export class AnthropicAppConfigValidationError extends ApiError {
  public readonly fieldErrors: Record<string, string[]>;
  public readonly formErrors: string[];
  constructor(fieldErrors: Record<string, string[]>, formErrors: string[]) {
    super('Validación rechazada por el backend', 400);
    this.name = 'AnthropicAppConfigValidationError';
    this.fieldErrors = fieldErrors;
    this.formErrors = formErrors;
  }
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

/**
 * Respuesta de `GET /api/anthropic/app-config`.
 *
 * Si `configured: false` los demás campos no vienen. NUNCA incluye `apiKey`:
 * la key es write-only desde la UI.
 */
/** M2.9 — modos de auth. 'api_key' (default M1.10) o 'claude_code' (CLI Max). */
export const ANTHROPIC_AUTH_MODES = ['api_key', 'claude_code'] as const;
export type AnthropicAuthMode = (typeof ANTHROPIC_AUTH_MODES)[number];

export const AnthropicAppConfigSchema = z.object({
  configured: z.boolean(),
  // M2.9 — opcional para retrocompat con respuestas antiguas.
  authMode: z.enum(ANTHROPIC_AUTH_MODES).optional(),
  modelDefault: z.enum(ANTHROPIC_MODELS).optional(),
  monthlyBudgetEur: z.number().int().nonnegative().optional(),
  tokensConsumedCurrentMonth: z.number().int().nonnegative().optional(),
  currentMonthYYYYMM: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type AnthropicAppConfig = z.infer<typeof AnthropicAppConfigSchema>;

/**
 * Body para `PUT /api/anthropic/app-config`. La validación principal vive en
 * el backend; replicamos las reglas críticas aquí para feedback inmediato:
 *
 * - `apiKey`: formato Anthropic (`sk-ant-` + 20..200 chars [a-zA-Z0-9-_]).
 * - `modelDefault`: enum de modelos soportados.
 * - `monthlyBudgetEur`: int 0..1000.
 */
/**
 * Validación condicional según `authMode`:
 *   - api_key (default): exige apiKey con regex sk-ant-...
 *   - claude_code: apiKey opcional, puede ser ''. El backend la ignora.
 *
 * Usamos superRefine en lugar de discriminatedUnion para no romper consumidores
 * que ya envían el shape histórico {apiKey, modelDefault, monthlyBudgetEur}
 * (esos se interpretan como authMode='api_key' implícito).
 */
export const AnthropicAppConfigPutBody = z
  .object({
    apiKey: z.string().optional(),
    authMode: z.enum(ANTHROPIC_AUTH_MODES).optional(),
    modelDefault: z.enum(ANTHROPIC_MODELS, {
      errorMap: () => ({ message: 'Modelo no soportado' }),
    }),
    monthlyBudgetEur: z
      .number({ invalid_type_error: 'Debe ser un número' })
      .int('Debe ser un entero')
      .min(0, 'Mínimo 0')
      .max(1000, 'Máximo 1000'),
  })
  .superRefine((data, ctx) => {
    const mode = data.authMode ?? 'api_key';
    if (mode === 'api_key') {
      const key = data.apiKey ?? '';
      if (key.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['apiKey'],
          message: 'Obligatorio',
        });
        return;
      }
      if (!/^sk-ant-[a-zA-Z0-9\-_]{20,200}$/.test(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['apiKey'],
          message:
            'Formato de API key de Anthropic inválido (debe empezar por sk-ant-)',
        });
      }
    }
  });
export type AnthropicAppConfigPutBody = z.infer<typeof AnthropicAppConfigPutBody>;

/**
 * Respuesta de `PUT /api/anthropic/app-config`. Mismo shape que el GET más
 * `apiKeyChanged`: `true` cuando el upsert detectó cambio de key (y por tanto
 * reseteó `tokensConsumedCurrentMonth`).
 */
export const AnthropicAppConfigPutResponseSchema = AnthropicAppConfigSchema.extend({
  apiKeyChanged: z.boolean().optional(),
});
export type AnthropicAppConfigPutResponse = z.infer<
  typeof AnthropicAppConfigPutResponseSchema
>;

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function pickString(body: unknown, key: string): string | undefined {
  if (body && typeof body === 'object' && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export async function getAnthropicAppConfig(): Promise<AnthropicAppConfig> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/anthropic/app-config`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(
      `Respuesta no OK del backend: ${response.status}`,
      response.status,
    );
  }
  const json = await readJsonSafe(response);
  const parsed = AnthropicAppConfigSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      'Respuesta /api/anthropic/app-config no cumple esquema',
      response.status,
      parsed.error,
    );
  }
  return parsed.data;
}

export async function putAnthropicAppConfig(
  body: AnthropicAppConfigPutBody,
): Promise<AnthropicAppConfigPutResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/anthropic/app-config`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.status === 200) {
    const json = await readJsonSafe(response);
    const parsed = AnthropicAppConfigPutResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(
        'Respuesta /api/anthropic/app-config (PUT) no cumple esquema',
        200,
        parsed.error,
      );
    }
    return parsed.data;
  }

  if (response.status === 400) {
    const json = await readJsonSafe(response);
    const issues =
      json && typeof json === 'object' && 'issues' in json
        ? (json as { issues?: unknown }).issues
        : null;
    const fieldErrors: Record<string, string[]> = {};
    const formErrors: string[] = [];
    if (issues && typeof issues === 'object') {
      const i = issues as { fieldErrors?: unknown; formErrors?: unknown };
      if (i.fieldErrors && typeof i.fieldErrors === 'object') {
        for (const [k, v] of Object.entries(
          i.fieldErrors as Record<string, unknown>,
        )) {
          if (Array.isArray(v)) {
            fieldErrors[k] = v.filter((x): x is string => typeof x === 'string');
          }
        }
      }
      if (Array.isArray(i.formErrors)) {
        for (const v of i.formErrors) {
          if (typeof v === 'string') formErrors.push(v);
        }
      }
    }
    throw new AnthropicAppConfigValidationError(fieldErrors, formErrors);
  }

  throw new ApiError(
    `Respuesta no OK del backend: ${response.status}`,
    response.status,
  );
}

export async function deleteAnthropicAppConfig(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/anthropic/app-config`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  // 204 esperado; 200 también lo aceptamos por defensa. Idempotente.
  if (response.status === 204 || response.ok) return;
  throw new ApiError(
    `Respuesta no OK del backend: ${response.status}`,
    response.status,
  );
}

// Re-export interno para tests / consumidores que necesiten mapear errores 503
// del endpoint del coach cuando vienen con `error: 'anthropic_*'`. Es seguro
// porque sólo expone el factory de string → ErrorClass.
export function mapAnthropicErrorCode(
  errCode: string,
  hint = '',
): AnthropicNotConfiguredError | AnthropicBudgetExceededError | null {
  if (
    errCode === 'anthropic_no_configurado' ||
    errCode === 'anthropic_not_configured' ||
    errCode === 'coach_not_configured'
  ) {
    return new AnthropicNotConfiguredError(hint);
  }
  if (errCode === 'anthropic_budget_exceeded') {
    return new AnthropicBudgetExceededError(hint);
  }
  return null;
}

// Pequeño helper compartido para no obligar a importar pickString fuera.
export function _readAnthropicErrorHint(body: unknown): string {
  return pickString(body, 'hint') ?? '';
}

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

export const ANTHROPIC_APP_CONFIG_QUERY_KEY = ['anthropic', 'app-config'] as const;

export function useAnthropicAppConfig(): UseQueryResult<AnthropicAppConfig, Error> {
  return useQuery<AnthropicAppConfig, Error>({
    queryKey: ANTHROPIC_APP_CONFIG_QUERY_KEY,
    queryFn: getAnthropicAppConfig,
    staleTime: 0,
    retry: 1,
  });
}

export function useUpdateAnthropicAppConfig(): UseMutationResult<
  AnthropicAppConfigPutResponse,
  Error,
  AnthropicAppConfigPutBody
> {
  const qc = useQueryClient();
  return useMutation<AnthropicAppConfigPutResponse, Error, AnthropicAppConfigPutBody>({
    mutationFn: (body) => putAnthropicAppConfig(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ANTHROPIC_APP_CONFIG_QUERY_KEY });
    },
  });
}

export function useDeleteAnthropicAppConfig(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => deleteAnthropicAppConfig(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ANTHROPIC_APP_CONFIG_QUERY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers expuestos a la UI
// ---------------------------------------------------------------------------

/** Convierte tokens consumidos a EUR aproximados (truncado a 2 decimales). */
export function tokensToEurApprox(tokens: number): number {
  if (!Number.isFinite(tokens) || tokens <= 0) return 0;
  return Math.round((tokens / TOKENS_PER_EUR) * 100) / 100;
}

/**
 * Devuelve el porcentaje (0..100) de presupuesto consumido. Si el budget es
 * 0 (caso degenerado), devolvemos 100 cuando hay cualquier consumo y 0 cuando
 * no hay nada, para que la UI muestre "alarma roja" si se intentó gastar sin
 * presupuesto asignado.
 */
export function budgetPercentUsed(
  tokensConsumed: number,
  monthlyBudgetEur: number,
): number {
  const totalBudgetTokens = monthlyBudgetEur * TOKENS_PER_EUR;
  if (!Number.isFinite(totalBudgetTokens) || totalBudgetTokens <= 0) {
    return tokensConsumed > 0 ? 100 : 0;
  }
  const pct = (tokensConsumed / totalBudgetTokens) * 100;
  if (!Number.isFinite(pct) || pct < 0) return 0;
  if (pct > 100) return 100;
  return pct;
}

/** Etiqueta legible + coste relativo de cada modelo, para el selector. */
export function modelLabel(model: AnthropicModel): string {
  switch (model) {
    case 'claude-sonnet-4-6':
      return 'Sonnet (balance) · ~5€/M tokens';
    case 'claude-haiku-4-5':
      return 'Haiku (rápido y barato) · ~1.5€/M tokens';
    case 'claude-opus-4-6':
      return 'Opus (máxima calidad) · ~25€/M tokens';
  }
}
